import { ArnHttpServiceFactory } from './http/ArnHttpServiceFactory';
import { ArnHttpService } from './http';
import { ArnFetchHttpService } from './http/ArnFetchHttpService';
import { ArnClient } from './ArnClient';
import { ArnClientFactory, ArnServiceFactories } from './ArnClientFactory';
import { ArnHttpI18nServiceFactory } from './i18n/ArnHttpI18nServiceFactory';
import { DefaultArnAuthServiceFactory } from './auth/DefaultArnAuthServiceFactory';
import { HttpNftServiceFactory } from './nft/HttpNftServiceFactory';
import { ArnClientConfig } from './ArnClientConfig';
import { ArnClientError } from './ArnClientError';
import { DefaultConditionServiceFactory } from './condition/DefaultConditionServiceFactory';
import { DefaultArnTrackingServiceFactory } from './track/DefaultArnTrackingServiceFactory';
import { DefaultArnDataServiceFactory } from './data/DefaultArnDataServiceFactory';
import { DefaultLoggerFactory } from './log/DefaultLoggerFactory';
import { ArnHttpClientConfigImpl } from './ArnHttpClientConfigImpl';
import {
  ArnClientServiceIndexed,
  ArnClientServiceKey,
  ArnValidClientConfig,
  ObjectUtil,
} from '@arianee/arn-types';
import {
  ArnHttpClientConfig,
  ArnValidHttpClientConfig,
} from './ArnHttpClientConfig';

export class FetchHttpServiceFactory implements ArnHttpServiceFactory {
  create(): ArnHttpService {
    return new ArnFetchHttpService();
  }
}

/**
 * Creates a client that will connect to an ARN Server.
 */
export class ArnHttpClientFactory
  implements ArnClientFactory<ArnHttpClientConfig>
{
  /**
   * Default implementations of service factories.
   *
   * They are construction methods instead of instances
   * to avoid instantiating them if they are to be replaced.
   */
  static readonly defaultServiceFactories: ArnClientServiceIndexed<() => any> =
    {
      auth: () => new DefaultArnAuthServiceFactory(),
      condition: () => new DefaultConditionServiceFactory(),
      data: () => new DefaultArnDataServiceFactory(),
      http: () => new FetchHttpServiceFactory(),
      i18n: () => new ArnHttpI18nServiceFactory(),
      log: () => new DefaultLoggerFactory(),
      nft: () => new HttpNftServiceFactory(),
      track: () => new DefaultArnTrackingServiceFactory(),
    };

  /**
   * Factories to instantiate services and their configurations.
   *
   * @protected
   */
  protected serviceFactories: ArnServiceFactories;

  /**
   * Creates an ARN Client factory.
   *
   * @param serviceFactories The custom service factories, if any.
   */
  constructor(serviceFactories: Partial<ArnServiceFactories> = {}) {
    const s: any = {};
    for (const p in ArnHttpClientFactory.defaultServiceFactories) {
      const key = p as ArnClientServiceKey;
      s[p] =
        serviceFactories[key] ||
        ArnHttpClientFactory.defaultServiceFactories[key]();
    }
    this.serviceFactories = s as ArnServiceFactories;
  }

  /**
   * Create an ARN Client, with some configuration spec.
   *
   * @param spec A partial configuration that will be completed by defaults.
   */
  create(spec: ArnHttpClientConfig): ArnClient {
    const serverUrl = ObjectUtil.asSet(
      spec.serverUrl,
      () =>
        new ArnClientError('serverUrl is mandatory to connect to an ARN server')
    );
    const config = this.createConfig(spec, serverUrl);
    const log = this.serviceFactories.log.create(config.log);
    const track = this.serviceFactories.track.create(config.track);
    const http = this.serviceFactories.http.create();
    const auth = this.serviceFactories.auth.create(
      log,
      http,
      serverUrl,
      config.auth
    );
    const condition = this.serviceFactories.condition.create(
      http,
      auth,
      config.condition,
      serverUrl
    );
    const nft = this.serviceFactories.nft.create(
      log,
      http,
      auth,
      serverUrl,
      config.nft
    );
    const i18n = this.serviceFactories.i18n.create(
      http,
      config.i18n,
      serverUrl
    );
    const data = this.serviceFactories.data.create(
      http,
      auth,
      config.data,
      serverUrl
    );
    const arnClient = new ArnClient(
      auth,
      condition,
      data,
      i18n,
      nft,
      track,
      log
    );
    if (config.autoRegister) {
      (window as any).arnClient = arnClient;
    }
    return arnClient;
  }

  /**
   * Utility method to converts a configuration with optionals
   * to a valid configuration with possible defaults applied.
   *
   * @param spec The config values to override defaults.
   * @param serverUrl The server to connect to, if config does not use absolute urls.
   */
  createConfig(
    spec: ArnClientConfig,
    serverUrl: string
  ): ArnValidHttpClientConfig {
    const track = this.serviceFactories.track.createConfig(spec.track);
    const i18n = this.serviceFactories.i18n.createConfig(spec.i18n, serverUrl);
    const auth = this.serviceFactories.auth.createConfig(spec.auth, serverUrl);
    const nft = this.serviceFactories.nft.createConfig(spec.nft, serverUrl);
    const data = this.serviceFactories.data.createConfig(spec.data, serverUrl);
    const condition = this.serviceFactories.condition.createConfig(
      spec.condition,
      serverUrl
    );
    const log = this.serviceFactories.log.createConfig(spec.log);
    const autoRegister = spec.autoRegister || true;
    return new ArnHttpClientConfigImpl(
      serverUrl,
      track,
      i18n,
      auth,
      nft,
      data,
      condition,
      log,
      autoRegister
    );
  }

  /**
   * Configuration cache, by API key.
   */
  readonly configByUrl = new Map<string, ArnValidClientConfig>();

  /**
   * Create an ARN Client associated with a given remote project.
   *
   * @param projectUrl The URL of the project.
   */
  async createFromUrl(projectUrl: string): Promise<ArnClient> {
    const config = await this.createConfigFromUrl(projectUrl);
    return this.create(config);
  }

  /**
   * Fetch a remote ARN project configuration.
   *
   * @param projectUrl The URL of the project.
   */
  async createConfigFromUrl(projectUrl: string): Promise<ArnValidClientConfig> {
    let config = this.configByUrl.get(projectUrl);
    if (!config) {
      const http = this.serviceFactories.http.create();
      const fetchedConfig = await http.fetchJson<ArnClientConfig>(projectUrl);
      try {
        config = this.createConfig(fetchedConfig, projectUrl);
        this.configByUrl.set(projectUrl, config);
      } catch (e) {
        throw new ArnClientError(
          `Error while reading ARN configuration from ${projectUrl}: ${e})`
        );
      }
    }
    return config;
  }
}
